import React from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import Badge from "@ui/Badge";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { getBadgeStableColors } from "@ui/Badge/utils";
import Link from "@ui/Link";
import formatDate from "@ui/i18n/formatDate";
import t from "../../../../../../../i18n";
import { toPath } from "../../../../../../../router/helpers/path";
import { getEventStatusText } from "../../../../utils/faultEventStatus";
import { getFaultEventDetectionText } from "../../../../utils/faultEventDetection";
import type { HistoryGroup } from "../../../../../../../api/events/histories";
import { Divider } from "@ui/Highcharts/ui/Tooltip";

type Props = {
  histories: HistoryGroup[];
  projectTimezone: string;
};

const HistoryContent = ({ histories, projectTimezone }: Props) => (
  <div className={historyContent}>
    {histories.map((history, index) => (
      <React.Fragment key={history.projectId}>
        {index > 0 && <Divider className={historyEntryDivider} />}
        <div className={historyTitle}>
          <Text type="body1SemiBold" title={history.projectName}>
            {history.projectName}
          </Text>
          {(history.siteId || history.typeId) && (
            <div className={badges}>
              {history.siteId ? (
                <Badge
                  style={getBadgeStableColors(history.siteId)}
                  className={badgeTextOverflow}
                  title={history.siteId}
                >
                  {history.siteId}
                </Badge>
              ) : null}
              {history.typeId ? (
                <Badge className={badgeTextOverflow} title={history.typeId}>
                  {history.typeId}
                </Badge>
              ) : null}
            </div>
          )}
        </div>
        {(history.events ?? []).length > 0 ? (
          <div className={historyCard}>
            {history.kind === "manual"
              ? history.events.map(e => {
                  const eventKey = e.id;
                  const eventLinkTarget = toPath(
                    "/projects/:projectId/fault-events/manual/:manualFaultEventId/fault",
                    {
                      projectId: history.projectId,
                      manualFaultEventId: e.id,
                    },
                  );
                  return (
                    <div key={eventKey}>
                      <Link className={eventLink} to={eventLinkTarget}>
                        <Text type="body1">{e.name}</Text>
                      </Link>
                      <Text type="body2">
                        {t("project.fault_event.side_panel.history.card.start_date", {
                          isClosed: e.status === "CLOSED",
                          startDate: formatDate(e.startsAt, "slash", projectTimezone),
                          endDate: formatDate(e.endsAt, "slash", projectTimezone),
                        })}
                        <span className={separator}>•</span>
                        {getEventStatusText(e.status)}
                      </Text>
                    </div>
                  );
                })
              : history.events.map(e => {
                  const eventKey = e.id;
                  const eventLinkTarget = toPath(
                    "/projects/:projectId/fault-events/:faultEventId/fault",
                    {
                      projectId: history.projectId,
                      faultEventId: e.id,
                    },
                  );
                  return (
                    <div key={eventKey}>
                      <Link className={eventLink} to={eventLinkTarget}>
                        <Text type="body1">{e.name}</Text>
                      </Link>
                      <Text type="body2">
                        {t("project.fault_event.side_panel.history.card.start_date", {
                          isClosed: e.status === "CLOSED",
                          startDate: formatDate(e.startsAt, "slash", projectTimezone),
                          endDate: formatDate(e.endsAt, "slash", projectTimezone),
                        })}
                        <span className={separator}>•</span>
                        {getEventStatusText(e.status)}
                        {e.status === "CLOSED" && e.detection !== null && (
                          <>
                            <span className={separator}>•</span>
                            {getFaultEventDetectionText(e.detection)}
                          </>
                        )}
                      </Text>
                    </div>
                  );
                })}
          </div>
        ) : null}
      </React.Fragment>
    ))}
  </div>
);

export default HistoryContent;

const historyContent = css`
  padding: ${spacings.m};
`;

const historyEntryDivider = css`
  margin: ${spacings.m} 0;
`;

const historyTitle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  color: ${colors.carbon700};
  gap: ${spacings.xs};

  & > :nth-child(1) {
    flex: 1 1 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const badges = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.xs};
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const historyCard = css`
  margin-top: ${spacings.m};

  & > div:not(:first-child) {
    margin-top: ${spacings.xs};
  }

  & > div > :nth-child(2) {
    /* subtitle - start date */
    margin-top: ${spacings.xxs};
    color: ${colors.carbon700};
  }
`;

const eventLink = css`
  color: ${colors.lightNitrogen500_Main};
  :hover {
    text-decoration: underline;
    color: ${colors.lightNitrogen600};
  }
`;

const separator = css`
  margin-left: ${spacings.xxs};
  margin-right: ${spacings.xxs};
`;
